import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Pagination,
  IconButton,
  Typography,
  Avatar,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Card,
  Chip,
  Rating,
  FormHelperText,
  Badge,
  Menu,
  Grid,
  Stack,
  Tabs,
  Tab,
} from '@mui/material';
import { 
  Delete, 
  Edit, 
  Search, 
  FilterList, 
  ArrowBack,
  Check, 
  Pending, 
  MoreVert,
  ThumbUp,
  ThumbDown,
  Block,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListReviewsQuery,
  useDeleteReviewMutation,
  useGetReviewCountQuery,
  useGetPendingReviewCountQuery,
  useGetRejectedReviewCountQuery,
  useAddReviewMutation,
  useUpdateReviewMutation,
  Review,
  ReviewFormData,
  FormErrors,
  SnackbarState,
} from '../../features/admin/reviewApi.ts';

const ReviewManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [page, setPage] = useState(1);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchStr, setSearchStr] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [productIdFilter, setProductIdFilter] = useState<string>('');
  const [sellerIdFilter, setSellerIdFilter] = useState<string>('');
  const [reviewTypeFilter, setReviewTypeFilter] = useState<string>('all');
  const [snackbar, setSnackbar] = useState<SnackbarState>({ 
    open: false, 
    message: '', 
    severity: 'success'
  });

  // Form related states
  const [showForm, setShowForm] = useState(false);
  const [editingReview, setEditingReview] = useState<Review | null>(null);
  const [formData, setFormData] = useState<ReviewFormData>({
    product_id: 0,
    seller_id: null,
    rating: 0,
    comment: '',
    status: 0,
    type: 0,
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  // Quick action states
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedReviewId, setSelectedReviewId] = useState<number | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<number | null>(null);

  const reviewsPerPage = 10;

  // Build query parameters with proper filtering
  const queryParams: any = {
    page, 
    limit: reviewsPerPage,
  };

  // Add search string if provided
  if (searchStr.trim()) {
    queryParams.search_str = searchStr.trim();
  }

  // Add status filter based on tab
  if (tabValue === 1) {
    queryParams.status = 0; // Pending tab
  } else if (tabValue === 2) {
    queryParams.status = 1; // Approved tab
  } else if (tabValue === 3) {
    queryParams.status = 2; // Rejected tab (NEW)
  }
  // tabValue === 0 means "All", so no status filter

  // Add product ID filter
  if (productIdFilter && parseInt(productIdFilter) > 0) {
    queryParams.product_id = parseInt(productIdFilter);
  }

  // Add seller ID filter
  if (sellerIdFilter && parseInt(sellerIdFilter) > 0) {
    queryParams.seller_id = parseInt(sellerIdFilter);
  }

  // Add type filter
  if (reviewTypeFilter === 'product') {
    queryParams.type = 0;
  } else if (reviewTypeFilter === 'seller') {
    queryParams.type = 1;
  }

  // Count params should match query params exactly
  const countParams = { ...queryParams };
  delete countParams.page;
  delete countParams.limit;

  // API hooks
  const { 
    data: reviewsResponse, 
    isLoading, 
    error, 
    isError, 
    refetch
  } = useListReviewsQuery(queryParams);

  const {
    data: countResponse,
    refetch: refetchCount
  } = useGetReviewCountQuery(countParams);

  const {
    data: pendingCountResponse,
    refetch: refetchPendingCount
  } = useGetPendingReviewCountQuery();

  const {
    data: rejectedCountResponse,
    refetch: refetchRejectedCount
  } = useGetRejectedReviewCountQuery();

  const [deleteReview] = useDeleteReviewMutation();
  const [addReview] = useAddReviewMutation();
  const [updateReview] = useUpdateReviewMutation();

  // Handle API response structure
  const reviews = reviewsResponse?.result || reviewsResponse?.data || [];
  const totalRecords = countResponse?.count || 0;
  const totalPages = Math.ceil(totalRecords / reviewsPerPage);
  const pendingCount = pendingCountResponse?.count || 0;
  const rejectedCount = rejectedCountResponse?.count || 0;

  const isEditing = Boolean(editingReview);

  // Debug logging
  useEffect(() => {
    console.log('Current Tab:', tabValue);
    console.log('Query Params:', queryParams);
    console.log('Reviews Response:', reviewsResponse);
    console.log('Reviews:', reviews);
  }, [tabValue, reviewsResponse]);

  // Debounced search effect
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setPage(1);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchStr, reviewTypeFilter, productIdFilter, sellerIdFilter, tabValue]);

  const handleChangePage = useCallback((event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen(!mobileOpen);
  }, [mobileOpen]);

  const handleSearch = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchStr(event.target.value);
  }, []);

  const handleProductIdFilterChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setProductIdFilter(event.target.value);
  }, []);

  const handleSellerIdFilterChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSellerIdFilter(event.target.value);
  }, []);

  const handleReviewTypeFilterChange = useCallback((event: SelectChangeEvent) => {
    setReviewTypeFilter(event.target.value);
  }, []);

  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(1);
  }, []);

  // Snackbar close handler
  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // Form handling functions
  const resetForm = useCallback(() => {
    setFormData({
      product_id: 0,
      seller_id: null,
      rating: 0,
      comment: '',
      status: 0,
      type: 0,
    });
    setFormErrors({});
    setEditingReview(null);
  }, []);

  const handleAddReview = useCallback(() => {
    resetForm();
    setShowForm(true);
  }, [resetForm]);

  const handleEditReview = useCallback((review: Review) => {
    setFormData({
      product_id: review.product_id,
      seller_id: review.seller_id,
      rating: review.rating,
      comment: review.comment,
      status: review.status,
      type: review.type || (review.seller_id && review.seller_id > 0 ? 1 : 0),
    });
    setEditingReview(review);
    setShowForm(true);
  }, []);

  const handleBackToList = useCallback(() => {
    setShowForm(false);
    resetForm();
  }, [resetForm]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      let updatedValue;
      
      if (name === 'product_id' || name === 'rating' || name === 'status') {
        updatedValue = value === '' ? 0 : Number(value);
      } else if (name === 'seller_id') {
        updatedValue = value === '' ? null : Number(value);
        const type = value === '' ? 0 : 1;
        return { 
          ...prev, 
          [name]: updatedValue,
          type: type
        };
      } else {
        updatedValue = value;
      }
      
      return { 
        ...prev, 
        [name]: updatedValue
      };
    });
    
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  }, [formErrors]);

  const handleStatusChange = useCallback((e: SelectChangeEvent) => {
    setFormData(prev => ({ ...prev, status: Number(e.target.value) }));
  }, []);

  const handleRatingChange = useCallback((event: React.SyntheticEvent, newValue: number | null) => {
    if (newValue !== null) {
      setFormData(prev => ({ ...prev, rating: newValue }));
    }
    if (formErrors.rating) {
      setFormErrors(prev => ({ ...prev, rating: '' }));
    }
  }, [formErrors]);

  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    if (!isEditing) {
      if (!formData.product_id || formData.product_id <= 0) {
        newErrors.product_id = 'Product ID is required';
      }

      if (formData.rating <= 0 || formData.rating > 5) {
        newErrors.rating = 'Rating must be between 1 and 5';
      }

      if (!formData.comment || !formData.comment.trim()) {
        newErrors.comment = 'Comment is required';
      }
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, isEditing]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsFormSubmitting(true);

    try {
      let result;
      if (isEditing && editingReview) {
        // Use review.id for update
        const reviewId = editingReview.id;
        console.log('Updating review with id:', reviewId);
        result = await updateReview({
          review_id: reviewId,
          status: formData.status
        }).unwrap();
        
        setSnackbar({
          open: true,
          message: result.message || 'Review updated successfully!',
          severity: 'success'
        });
      } else {
        const reviewType = formData.seller_id ? 1 : 0;
        
        const addReviewData = {
          product_id: formData.product_id,
          seller_id: formData.seller_id,
          rating: formData.rating,
          comment: formData.comment,
          type: reviewType
        };
        
        console.log('Adding review:', addReviewData);
        result = await addReview(addReviewData).unwrap();
        
        setSnackbar({
          open: true,
          message: result.message || 'Review added successfully!',
          severity: 'success'
        });
      }
      
      handleBackToList();
      
      // Refetch all data
      await Promise.all([
        refetch(),
        refetchCount(),
        refetchPendingCount(),
        refetchRejectedCount()
      ]);
      
    } catch (error: any) {
      console.error('Submit error:', error);
      setSnackbar({
        open: true,
        message: error.data?.message || error.message || `Error ${isEditing ? 'updating' : 'adding'} review.`,
        severity: 'error'
      });
    } finally {
      setIsFormSubmitting(false);
    }
  }, [validateForm, isEditing, editingReview, formData, updateReview, addReview, handleBackToList, refetch, refetchCount, refetchPendingCount, refetchRejectedCount]);

  const handleDelete = useCallback(async (reviewId: number) => {
    if (window.confirm('Are you sure you want to delete this review?')) {
      try {
        console.log('Deleting review with id:', reviewId);
        const result = await deleteReview({ review_id: reviewId }).unwrap();
        setSnackbar({
          open: true,
          message: result.message || 'Review deleted successfully!',
          severity: 'success'
        });
        
        // Refetch all data
        await Promise.all([
          refetch(),
          refetchCount(),
          refetchPendingCount(),
          refetchRejectedCount()
        ]);
        
      } catch (err: any) {
        const errorMessage = err?.data?.message || err?.message || 'Error deleting review.';
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      }
    }
  }, [deleteReview, refetch, refetchCount, refetchPendingCount, refetchRejectedCount]);

  // Quick status update - Use review.id
  const handleQuickStatusUpdate = useCallback(async (review: Review, newStatus: number) => {
    const reviewId = review.id;
    console.log('Quick updating review status with id:', reviewId, 'to status:', newStatus);
    setUpdatingStatus(reviewId);
    
    try {
      const result = await updateReview({
        review_id: reviewId,
        status: newStatus
      }).unwrap();
      
      let statusMessage = '';
      switch (newStatus) {
        case 0:
          statusMessage = 'marked as pending';
          break;
        case 1:
          statusMessage = 'approved';
          break;
        case 2:
          statusMessage = 'rejected';
          break;
        default:
          statusMessage = 'updated';
      }
      
      setSnackbar({
        open: true,
        message: result.message || `Review ${statusMessage} successfully!`,
        severity: 'success'
      });
      
      setActionMenuAnchor(null);
      setSelectedReviewId(null);
      
      // Refetch all data
      await Promise.all([
        refetch(),
        refetchCount(),
        refetchPendingCount(),
        refetchRejectedCount()
      ]);
      
    } catch (error: any) {
      console.error('Status update error:', error);
      setSnackbar({
        open: true,
        message: error.data?.message || 'Error updating review status.',
        severity: 'error'
      });
    } finally {
      setUpdatingStatus(null);
    }
  }, [updateReview, refetch, refetchCount, refetchPendingCount, refetchRejectedCount]);

  const handleActionMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>, reviewId: number) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedReviewId(reviewId);
  }, []);

  const handleActionMenuClose = useCallback(() => {
    setActionMenuAnchor(null);
    setSelectedReviewId(null);
  }, []);

  // Status rendering
  const renderStatus = useCallback((status: number) => {
    switch (status) {
      case 1:
        return <Chip label="Approved" size="small" sx={{ bgcolor: '#d1f4e0', color: '#00783e', fontWeight: 600, fontSize: '12px' }} />;
      case 0:
        return <Chip label="Pending" size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '12px' }} />;
      case 2:
        return <Chip label="Rejected" size="small" sx={{ bgcolor: '#ffebee', color: '#c62828', fontWeight: 600, fontSize: '12px' }} />;
      default:
        return <Chip label="Pending" size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '12px' }} />;
    }
  }, []);

  // Review type rendering
  const renderReviewType = useCallback((review: Review) => {
    const isSeller = review.type === 1 || (review.seller_id && review.seller_id > 0);
    
    if (isSeller) {
      return <Chip label="Seller Review" size="small" sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600, fontSize: '11px' }} />;
    } else {
      return <Chip label="Product Review" size="small" sx={{ bgcolor: '#f3e5f5', color: '#6a1b9a', fontWeight: 600, fontSize: '11px' }} />;
    }
  }, []);

  // Status icon rendering
  const renderStatusIcon = useCallback((status: number) => {
    switch (status) {
      case 1:
        return <ThumbUp fontSize="small" />;
      case 0:
        return <Pending fontSize="small" />;
      case 2:
        return <Block fontSize="small" />;
      default:
        return <Pending fontSize="small" />;
    }
  }, []);

  const formatDate = useCallback((dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  }, []);

  const clearFilters = useCallback(() => {
    setSearchStr('');
    setProductIdFilter('');
    setSellerIdFilter('');
    setReviewTypeFilter('all');
    setTabValue(0);
    setPage(1);
  }, []);

  // Function to get available status options for a review
  const getAvailableStatusOptions = useCallback((currentStatus: number) => {
    const allStatuses = [
      { value: 0, label: 'Pending', icon: <Pending />, color: '#e65100' },
      { value: 1, label: 'Approve', icon: <ThumbUp />, color: '#00783e' },
      { value: 2, label: 'Reject', icon: <Block />, color: '#c62828' }
    ];
    
    // Return all statuses except the current one
    return allStatuses.filter(status => status.value !== currentStatus);
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu 
            mobileOpen={false} 
            onToggle={handleDrawerToggle} 
            pendingReviewCount={pendingCount}
            rejectedReviewCount={rejectedCount}
          />
        </Box>
        
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu 
            onToggle={handleDrawerToggle} 
            mobileOpen={false} 
            pendingReviewCount={pendingCount}
            rejectedReviewCount={rejectedCount}
          />
        </Drawer>
        
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              backgroundColor: '#f1f2f4',
            }}
          >
            {!showForm ? (
              // LIST VIEW
              <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 2
                }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    Review
                  </Typography>
                  
                  <Button
                    variant="contained"
                    onClick={handleAddReview}
                    sx={{
                      bgcolor: '#202223',
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '8px',
                      px: 2.5,
                      '&:hover': { bgcolor: '#303030' }
                    }}
                  >
                    Add review
                  </Button>
                </Box>

                <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                  <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                    <Tabs 
                      value={tabValue} 
                      onChange={handleTabChange}
                      sx={{
                        px: 2,
                        '& .MuiTab-root': {
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '14px',
                          minHeight: 48,
                          color: '#6d7175',
                        },
                        '& .Mui-selected': {
                          color: '#202223',
                          fontWeight: 600,
                        },
                        '& .MuiTabs-indicator': {
                          backgroundColor: '#202223',
                          height: 3,
                        }
                      }}
                    >
                      <Tab label="All" />
                     

                    </Tabs>
                  </Box>

                  <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="Search reviews..."
                      value={searchStr}
                      onChange={handleSearch}
                      sx={{ 
                        minWidth: 200,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          bgcolor: 'white',
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" sx={{ color: '#6d7175' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <Select
                        value={reviewTypeFilter}
                        onChange={handleReviewTypeFilterChange}
                        displayEmpty
                        sx={{ 
                          borderRadius: '8px',
                          bgcolor: 'white',
                        }}
                      >
                        <MenuItem value="all">All Types</MenuItem>
                        <MenuItem value="product">Product Reviews</MenuItem>
                        <MenuItem value="seller">Seller Reviews</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Product ID"
                      value={productIdFilter}
                      onChange={handleProductIdFilterChange}
                      sx={{ 
                        minWidth: 120,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          bgcolor: 'white',
                        }
                      }}
                      InputProps={{
                        inputProps: { min: 1 },
                      }}
                    />
                    
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Seller ID"
                      value={sellerIdFilter}
                      onChange={handleSellerIdFilterChange}
                      sx={{ 
                        minWidth: 120,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          bgcolor: 'white',
                        }
                      }}
                      InputProps={{
                        inputProps: { min: 1 },
                      }}
                    />
                    
                    <Button
                      variant="outlined"
                      startIcon={<FilterList />}
                      onClick={clearFilters}
                      sx={{
                        textTransform: 'none',
                        borderRadius: '8px',
                        borderColor: '#e1e3e5',
                        color: '#202223',
                        '&:hover': {
                          borderColor: '#202223',
                          bgcolor: 'transparent'
                        }
                      }}
                    >
                      Clear filters
                    </Button>
                  </Box>

                  {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                      <CircularProgress size={32} sx={{ color: '#008060' }} />
                    </Box>
                  )}

                  {isError && !isLoading && (
                    <Box sx={{ p: 3, bgcolor: 'white' }}>
                      <Alert severity="error">
                        Failed to load reviews. {error && 'message' in error ? error.message : 'Please try again.'}
                      </Alert>
                    </Box>
                  )}

                  {!isLoading && reviews.length === 0 && (
                    <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'white' }}>
                      <Typography color="text.secondary">
                        No reviews found. {tabValue > 0 && 'Try changing the filter or search criteria.'}
                      </Typography>
                    </Box>
                  )}

                  {!isLoading && reviews.length > 0 && (
                    <Box sx={{ bgcolor: 'white' }}>
                      {/* Table Header */}
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: isMobile ? '1fr' : '50px 1fr 120px 100px 100px 150px 180px 120px 180px 100px',
                        gap: 2,
                        px: 2,
                        py: 1.5,
                        borderBottom: '1px solid #e1e3e5',
                        bgcolor: '#fafbfb',
                        display: { xs: 'none', md: 'grid' }
                      }}>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          #
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Customer & Comment
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Type
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Product ID
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Seller ID
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Rating
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Date
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Status
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Quick Actions
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', textAlign: 'right' }}>
                          Actions
                        </Typography>
                      </Box>

                      {/* Table Rows */}
                      {reviews.map((review: Review, index: number) => {
                        const reviewId = review.id;
                        const availableStatuses = getAvailableStatusOptions(review.status);
                        
                        return (
                          <Box
                            key={reviewId}
                            sx={{
                              display: { xs: 'block', md: 'grid' },
                              gridTemplateColumns: '50px 1fr 120px 100px 100px 150px 180px 120px 180px 100px',
                              gap: 2,
                              px: 2,
                              py: 2,
                              alignItems: 'center',
                              borderBottom: index < reviews.length - 1 ? '1px solid #e1e3e5' : 'none',
                              '&:hover': { bgcolor: '#f9fafb' },
                              transition: 'background-color 0.15s ease',
                            }}
                          >
                            <Box sx={{ 
                              display: { xs: 'flex', md: 'grid' },
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              mb: { xs: 1, md: 0 }
                            }}>
                              <Typography variant="body2" sx={{ color: '#6d7175' }}>
                                {(page - 1) * reviewsPerPage + index + 1}
                              </Typography>
                              <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1 }}>
                                {renderStatus(review.status)}
                                {renderReviewType(review)}
                              </Box>
                            </Box>

                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Avatar
                                  sx={{ 
                                    width: 32, 
                                    height: 32, 
                                    bgcolor: '#f1f2f4',
                                    fontSize: '14px'
                                  }}
                                >
                                  {(review.customer_name || 'A')[0].toUpperCase()}
                                </Avatar>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>
                                  {review.customer_name || 'Anonymous'}
                                </Typography>
                              </Box>
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{
                                  fontSize: '13px',
                                  lineHeight: 1.3,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden'
                                }}
                              >
                                {review.comment}
                              </Typography>
                            </Box>

                            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                              {renderReviewType(review)}
                            </Box>

                            <Typography variant="body2" sx={{ color: '#202223', fontWeight: 500, display: { xs: 'none', md: 'block' } }}>
                              {review.product_id}
                            </Typography>

                            <Typography variant="body2" sx={{ color: '#202223', fontWeight: 500, display: { xs: 'none', md: 'block' } }}>
                              {review.seller_id || '-'}
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Rating value={review.rating} readOnly size="small" />
                              <Typography variant="body2" sx={{ color: '#6d7175', fontSize: '12px' }}>
                                ({review.rating})
                              </Typography>
                            </Box>

                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px', display: { xs: 'none', md: 'block' } }}>
                              {formatDate(review.created_date)}
                            </Typography>

                            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                              {renderStatus(review.status)}
                            </Box>

                            {/* Quick Actions */}
                            <Box sx={{ 
                              display: { xs: 'none', md: 'flex' }, 
                              gap: 0.5,
                              flexWrap: 'wrap'
                            }}>
                              {availableStatuses.map((statusOption) => (
                                <IconButton
                                  key={statusOption.value}
                                  size="small"
                                  onClick={() => handleQuickStatusUpdate(review, statusOption.value)}
                                  disabled={updatingStatus === reviewId}
                                  sx={{ 
                                    color: statusOption.color,
                                    '&:hover': { 
                                      bgcolor: statusOption.color + '10'
                                    },
                                    border: `1px solid ${statusOption.color}20`,
                                    borderRadius: '6px',
                                    width: '32px',
                                    height: '32px'
                                  }}
                                  title={`Mark as ${statusOption.label}`}
                                >
                                  {updatingStatus === reviewId ? (
                                    <CircularProgress size={14} />
                                  ) : (
                                    React.cloneElement(statusOption.icon, { fontSize: 'small' })
                                  )}
                                </IconButton>
                              ))}
                            </Box>

                            <Box sx={{ 
                              display: 'flex', 
                              gap: 0.5, 
                              justifyContent: { xs: 'flex-start', md: 'flex-end' },
                              mt: { xs: 1, md: 0 }
                            }}>
                              <IconButton
                                size="small"
                                onClick={(e) => handleActionMenuOpen(e, reviewId)}
                                sx={{ 
                                  color: '#6d7175',
                                  '&:hover': { bgcolor: '#f1f2f4' }
                                }}
                              >
                                <MoreVert fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Card>

                {totalPages > 1 && !isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handleChangePage}
                      sx={{
                        '& .MuiPaginationItem-root': {
                          borderRadius: '8px',
                          fontWeight: 500,
                        },
                        '& .Mui-selected': {
                          bgcolor: '#202223 !important',
                          color: 'white',
                        }
                      }}
                    />
                  </Box>
                )}
              </Box>
            ) : (
              // FORM VIEW
              <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <Button
                    startIcon={<ArrowBack />}
                    onClick={handleBackToList}
                    sx={{ 
                      color: '#202223',
                      textTransform: 'none',
                      fontWeight: 500,
                      mb: 2,
                      '&:hover': { bgcolor: 'transparent' }
                    }}
                  >
                    Review
                  </Button>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    {isEditing ? 'Edit review' : 'Add review'}
                  </Typography>
                </Box>

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <Stack spacing={2}>
                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                              Review details
                            </Typography>
                            
                            {!isEditing && (
                              <>
                                <TextField
                                  name="product_id"
                                  label="Product ID *"
                                  type="number"
                                  value={formData.product_id || ''}
                                  onChange={handleInputChange}
                                  error={!!formErrors.product_id}
                                  helperText={formErrors.product_id}
                                  fullWidth
                                  required
                                  placeholder="Enter product ID"
                                  disabled={isFormSubmitting}
                                  sx={{ mb: 2 }}
                                  InputProps={{
                                    inputProps: { min: 1 }
                                  }}
                                />

                                <TextField
                                  name="seller_id"
                                  label="Seller ID (Optional - for seller reviews)"
                                  type="number"
                                  value={formData.seller_id || ''}
                                  onChange={handleInputChange}
                                  fullWidth
                                  placeholder="Enter seller ID (leave empty for product review)"
                                  disabled={isFormSubmitting}
                                  sx={{ mb: 2 }}
                                  InputProps={{
                                    inputProps: { min: 1 }
                                  }}
                                  helperText={formData.seller_id ? "This will be saved as a seller review" : "This will be saved as a product review"}
                                />

                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#202223' }}>
                                    Rating *
                                  </Typography>
                                  <Rating
                                    name="rating"
                                    value={formData.rating}
                                    onChange={handleRatingChange}
                                    precision={0.5}
                                    size="large"
                                    disabled={isFormSubmitting}
                                  />
                                  {formErrors.rating && (
                                    <FormHelperText error>{formErrors.rating}</FormHelperText>
                                  )}
                                </Box>

                                <TextField
                                  name="comment"
                                  label="Comment *"
                                  value={formData.comment || ''}
                                  onChange={handleInputChange}
                                  error={!!formErrors.comment}
                                  helperText={formErrors.comment}
                                  fullWidth
                                  required
                                  multiline
                                  rows={4}
                                  placeholder="Enter your review comment"
                                  disabled={isFormSubmitting}
                                />
                              </>
                            )}

                            {isEditing && editingReview && (
                              <Box sx={{ p: 2, bgcolor: '#fafbfb', borderRadius: '8px', border: '1px solid #e1e3e5' }}>
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#202223' }}>
                                  CURRENT REVIEW (ID: {editingReview.id})
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                  <Avatar
                                    sx={{ width: 48, height: 48, bgcolor: '#f1f2f4' }}
                                  >
                                    {(editingReview.customer_name || 'A')[0].toUpperCase()}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>
                                      {editingReview.customer_name || 'Anonymous'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Product ID: {editingReview.product_id}
                                      {editingReview.seller_id && ` | Seller ID: ${editingReview.seller_id}`}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Rating value={editingReview.rating} readOnly size="small" />
                                  <Typography variant="body2" color="text.secondary">
                                    ({editingReview.rating})
                                  </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                  {editingReview.comment}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Card>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                        <Box sx={{ p: 3 }}>
                          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                            Status
                          </Typography>
                          <FormControl fullWidth disabled={isFormSubmitting}>
                            <InputLabel>Status</InputLabel>
                            <Select
                              value={formData.status?.toString() || '0'}
                              onChange={handleStatusChange}
                              label="Status"
                            >
                              <MenuItem value={0}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Pending sx={{ mr: 1, color: '#e65100' }} fontSize="small" />
                                  Pending
                                </Box>
                              </MenuItem>
                              <MenuItem value={1}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Check sx={{ mr: 1, color: '#00783e' }} fontSize="small" />
                                  Approved
                                </Box>
                              </MenuItem>
                              <MenuItem value={2}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Block sx={{ mr: 1, color: '#c62828' }} fontSize="small" />
                                  Rejected
                                </Box>
                              </MenuItem>
                            </Select>
                          </FormControl>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {formData.status === 1 ? "Review will be visible to users" : 
                             formData.status === 2 ? "Review will be marked as rejected" :
                             "Review will be pending approval"}
                          </Typography>
                        </Box>
                      </Card>
                    </Grid>
                  </Grid>

                  <Box sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    justifyContent: 'flex-end', 
                    mt: 3,
                    pt: 3,
                    borderTop: '1px solid #e1e3e5'
                  }}>
                    <Button
                      onClick={handleBackToList}
                      disabled={isFormSubmitting}
                      sx={{ 
                        textTransform: 'none', 
                        fontWeight: 600,
                        color: '#202223',
                        borderRadius: '8px',
                        px: 2.5
                      }}
                    >
                      Discard
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isFormSubmitting}
                      sx={{
                        bgcolor: '#008060',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '8px',
                        px: 2.5,
                        '&:hover': { bgcolor: '#006e52' }
                      }}
                    >
                      {isFormSubmitting ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={20} color="inherit" />
                          {isEditing ? 'Updating...' : 'Adding...'}
                        </Box>
                      ) : (
                        isEditing ? 'Update review' : 'Add review'
                      )}
                    </Button>
                  </Box>
                </form>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: { 
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            minWidth: 140
          }
        }}
      >
        <MenuItem
          onClick={() => {
            if (selectedReviewId) {
              const review = reviews.find(r => r.id === selectedReviewId);
              if (review) {
                handleEditReview(review);
              }
            }
            handleActionMenuClose();
          }}
          sx={{ fontSize: '14px' }}
        >
          <Edit sx={{ mr: 1 }} fontSize="small" />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedReviewId) {
              handleDelete(selectedReviewId);
            }
            handleActionMenuClose();
          }}
          sx={{ color: '#d32f2f', fontSize: '14px' }}
        >
          <Delete sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReviewManagement;